import {
    Autocomplete,
    Chip,
    ListItem,
    ListItemButton,
    TextField,
    Typography,
} from '@mui/material'
import { GetRostersByNameQuery } from '__generated__/graphql'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { usePagination } from 'shared/hooks/usePagination'
import { GetRostersByName } from './RostersAutocomplete.gql'
import { RostersAutocompleteProps } from './RostersAutocompleteProps'

const LIMIT = 5
/**
 * multiple + single autocompletes are separated to workaround MUI typing
 * @param RostersAutocompleteProps
 * @returns MUI Autocomplete with paginated options (multi-select)
 */
const RostersAutocompleteMultiple: FC<RostersAutocompleteProps<true>> = ({
    error,
    helperText,
    size,
    excludeValues = [],
    initialValue,
    allMyRosters,
    onlyMyRosters,
    includeDrafts,
    currentRosterId,
    includeClosed,
    stateManagedExternally = false,
    externalState = [],
    onMultipleSelectionChange,
    label = 'Searches',
    limitTags = 5,
    placeholder,
    ...props
}) => {
    const [name, setName] = useState('')
    const [open, setOpen] = useState(false)
    const [debouncedInput, setDebouncedInput] = useState<string>('')
    const [selectedRosters, setSelectedRosters] = useState<
        GetRostersByNameQuery['rostersByName']['data'][number][]
    >(
        !!initialValue
            ? Array.isArray(initialValue)
                ? initialValue
                : [initialValue]
            : []
    )

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedInput(name)
        }, 1000)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    useCallback(() => {
        refetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput])

    const divName = 'rostersMultiAutcomplete'
    const { data, loading, refetch } = usePagination<
        GetRostersByNameQuery['rostersByName']['data'][number]
    >({
        query: GetRostersByName,
        variables: {
            name: name,
            page: 1,
            limit: LIMIT,
            onlyMyRosters,
            includeAllMyRosters: allMyRosters,
            includeDrafts,
            includeClosedRosters: includeClosed,
        },
        queryPath: 'rostersByName',
        limit: LIMIT,
        skip: !open,
        trackedDivClassName: divName,
    })
    const options = useMemo(
        () =>
            data
                .filter(
                    (r) =>
                        r.id !== currentRosterId &&
                        !selectedRosters.some((sr) => sr.id === r.id) &&
                        !excludeValues.some((ev) => ev.id === r.id)
                )
                .map(({ __typename, ...rest }) => rest) || [],
        [data, excludeValues, selectedRosters, currentRosterId]
    )

    return (
        <Autocomplete
            fullWidth
            multiple
            className={divName}
            onOpen={() => setOpen(true)}
            onClose={(event, reason) => {
                if (reason === 'blur') {
                    setOpen(false) // Close only on blur
                }
            }}
            disablePortal
            getOptionLabel={(option) => option.rosterName}
            loading={loading}
            isOptionEqualToValue={(option, value) => {
                return option.id === value.id
            }}
            renderOption={(props, option, state, ownerState) => {
                return (
                    <ListItem
                        {...props}
                        sx={{
                            padding: 0,
                        }}
                        className={divName}
                        key={option.id}
                    >
                        <ListItemButton>{option.rosterName}</ListItemButton>
                    </ListItem>
                )
            }}
            renderTags={(value, getTagProps) => {
                const numTags = value.length
                return (
                    <>
                        {value.slice(0, limitTags).map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={index}
                                label={option.rosterName}
                            />
                        ))}

                        {numTags > limitTags && (
                            <Typography variant="detail">
                                {' '}
                                +{numTags - limitTags}{' '}
                            </Typography>
                        )}
                    </>
                )
            }}
            limitTags={limitTags}
            onChange={(event, newValue) => {
                if (!stateManagedExternally) setSelectedRosters(newValue)
                onMultipleSelectionChange?.(newValue)
            }}
            value={stateManagedExternally ? externalState : selectedRosters}
            onInputChange={(e, value, reason) => {
                if (reason === 'reset') setName('')
                else setName(value)
            }}
            {...props}
            options={options}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )}
        />
    )
}

export default RostersAutocompleteMultiple
